import React from 'react';
import styled from 'styled-components';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import {
    DatepickerTextField,
    DatepickerSinglePicker,
    TextField,
    Button,
    Autocomplete,
} from '@avangard/ui/core';

import { routes } from '@config/routes';
import { createRouteUrl, useNavigate } from '@lib/routing';
import { useRegions, useLazyLocations, useLazySchools } from '@modules/shared/hooks';
import { useEnqueueStacks } from '@modules/layout/hooks';
import { FormStyled } from '@modules/layout/styled';
import { ExtendedFormik, Form } from '@modules/layout/organisms';
import { gendersOptions } from '@modules/user/constants';
import { useCurrentUser } from '@modules/user/hooks';
import { CreateStudentRequest } from '@modules/students/requests';
import { CreateStudentMutation } from '@modules/students/graphql';
import { SchoolEntity } from '@modules/schools/entities';

import type { OnDateChangeProps } from '@avangard/ui/core';
import type { RegionEntity, LocationEntity } from '@modules/shared/entities';
import type { CreateStudentFormValues } from '@modules/students/requests';
import type {
    CreateStudentMutationType,
    CreateStudentMutationVariables,
} from '@modules/types/graphql';

type StudentCreateFormProps = {
    onCancel: () => void;
};

const StyledForm = styled(Form)`
    width: 100%;

    > * {
        &:not(:last-of-type) {
            margin-bottom: 24px;
        }
    }
`;

const FormRowSubmit = styled(FormStyled.FormRowSubmit)`
    justify-content: flex-end;
`;

const StudentCreateForm = (props: StudentCreateFormProps) => {
    const { onCancel } = props;

    const { t } = useTranslation(['common', 'errors', 'students']);

    const navigate = useNavigate();

    const { enqueueSuccess, enqueueError } = useEnqueueStacks();

    const { currentUser } = useCurrentUser();
    const hasAccessSelectSchool = !currentUser.isSchool();

    const { regions, loading: regionsLoading } = useRegions();
    const [getLocations, { locations, count: locationsCount, loading: locationsLoading }] =
        useLazyLocations();

    const [getSchools, { schools, count: schoolsCount, loading: schoolsLoading }] =
        useLazySchools();

    const [createStudent] = useMutation<CreateStudentMutationType, CreateStudentMutationVariables>(
        CreateStudentMutation,
    );

    const initialValues: CreateStudentFormValues = {
        regionId: '',
        schoolLocationId: '',
        schoolId: '',
        fullName: '',
        email: '',
        gender: '',
        birthdate: null,
    };

    const getOptionLabel =
        (field: string, withTranslate?: boolean) =>
        (option: LocationEntity | SchoolEntity | AutocompleteOption | null): string => {
            if (!option?.[field]) {
                return '-';
            }

            if (withTranslate) {
                return t(option[field]);
            }

            return option[field];
        };

    const getOptionSelected =
        () =>
        (
            option: RegionEntity | LocationEntity | SchoolEntity | AutocompleteOption | null,
            value: RegionEntity | LocationEntity | SchoolEntity | AutocompleteOption | null,
        ): boolean => {
            if (!option || !value) {
                return false;
            }

            if ('key' in option && 'key' in value) {
                return option.key === value.key;
            }

            if ('id' in option && 'id' in value) {
                return option.id === value.id;
            }

            return false;
        };

    const getRegionOptionValue = (option?: string | null) => {
        const idx = regions.findIndex(region => region.id === option);

        if (idx > -1) {
            return regions[idx];
        }

        return null;
    };

    const getLocationOptionValue = (option?: string | null) => {
        const idx = locations.findIndex(location => location.id === option);

        if (idx > -1) {
            return locations[idx];
        }

        return null;
    };

    const getSchoolOptionValue = (option?: string | null) => {
        const idx = schools.findIndex(school => school.id === option);

        if (idx > -1) {
            return schools[idx];
        }

        return null;
    };

    const getGenderOptionValue = (option?: string | null) => {
        const idx = gendersOptions.findIndex(gender => gender.key === option);

        if (idx > -1) {
            return gendersOptions[idx];
        }

        return null;
    };

    return (
        <ExtendedFormik
            validateOnChange={false}
            validateOnBlur={false}
            validationSchema={() => {
                const fields: Record<string, any> = {
                    fullName: yup.string().required(t('errors:validations.required') ?? ''),
                    email: yup.string().required(t('errors:validations.required') ?? ''),
                    birthdate: yup.string().required(t('errors:validations.required') ?? ''),
                    gender: yup.string().required(t('errors:validations.required') ?? ''),
                };

                if (hasAccessSelectSchool) {
                    fields.schoolLocationId = yup
                        .string()
                        .required(t('errors:validations.required') ?? '');
                    fields.schoolId = yup.string().required(t('errors:validations.required') ?? '');
                }

                return yup.object(fields);
            }}
            initialValues={initialValues}
            onSubmit={async values => {
                try {
                    const item = new CreateStudentRequest(values);

                    const { data: createStudentData } = await createStudent({
                        variables: { item },
                    });

                    const studentId = createStudentData?.createStudent?.id;

                    if (studentId) {
                        enqueueSuccess(t('students:notifiers.add_student.success'));

                        navigate(createRouteUrl(routes.studentsEditPage, { id: studentId }));
                    } else {
                        enqueueError(t('students:notifiers.add_student.error'));
                    }
                } catch (e) {
                    console.log(e);
                    throw e;
                }
            }}
        >
            {formikProps => {
                const {
                    values,
                    errors,
                    setValues,
                    setFieldValue,
                    handleChange,
                    handleBlur,
                    isSubmitting,
                } = formikProps;

                // TODO: move in separate form group
                React.useEffect(() => {
                    if (!values.regionId) {
                        return;
                    }

                    getLocations({ variables: { regionId: values.regionId } });
                }, [values.regionId]);

                React.useEffect(() => {
                    if (!values.schoolLocationId) {
                        return;
                    }

                    getSchools({
                        variables: {
                            args: {
                                filter: {
                                    location: {
                                        id: values.schoolLocationId,
                                    },
                                },
                                page: { all: true },
                            },
                        },
                    });
                }, [values.schoolLocationId]);

                const handleChangeDate = (data: OnDateChangeProps) =>
                    setFieldValue('birthdate', data.date);

                const handleChangeAutocomplete =
                    (field: string, by: string = 'id') =>
                    (_: React.ChangeEvent<{}>, value: any | null) => {
                        setValues(state => ({ ...state, [field]: value?.[by] }));

                        if (field === 'regionId') {
                            setFieldValue('location', null);
                        }
                    };

                return (
                    <StyledForm>
                        {hasAccessSelectSchool ? (
                            <>
                                <Autocomplete<RegionEntity | null, false, false, false>
                                    required
                                    id='regionId'
                                    label={t('common:forms.labels.region') ?? ''}
                                    placeholder={t('common:forms.placeholder.choose_region') ?? ''}
                                    options={regions}
                                    value={getRegionOptionValue(values.regionId)}
                                    error={!!errors.regionId}
                                    helperText={errors.regionId}
                                    disabled={regionsLoading}
                                    onChange={handleChangeAutocomplete('regionId')}
                                    getOptionLabel={getOptionLabel('name')}
                                    getOptionSelected={getOptionSelected()}
                                />

                                <Autocomplete<LocationEntity | null, false, false, false>
                                    required
                                    id='schoolLocationId'
                                    label={t('common:forms.labels.location') ?? ''}
                                    placeholder={
                                        t('common:forms.placeholder.choose_location') ?? ''
                                    }
                                    options={locations}
                                    value={getLocationOptionValue(values.schoolLocationId)}
                                    error={!!errors.schoolLocationId}
                                    helperText={errors.schoolLocationId}
                                    disabled={
                                        !values.regionId || locationsCount === 0 || locationsLoading
                                    }
                                    onChange={handleChangeAutocomplete('schoolLocationId')}
                                    getOptionLabel={getOptionLabel('name')}
                                    getOptionSelected={getOptionSelected()}
                                />

                                <Autocomplete<SchoolEntity | null, false, false, false>
                                    required
                                    id='schoolId'
                                    label={t('common:forms.labels.school_short_name') ?? ''}
                                    placeholder={
                                        t('common:forms.placeholder.choose_school_short_name') ?? ''
                                    }
                                    options={schools}
                                    value={getSchoolOptionValue(values.schoolId)}
                                    error={!!errors.schoolId}
                                    helperText={errors.schoolId}
                                    disabled={
                                        !values.schoolLocationId ||
                                        schoolsCount === 0 ||
                                        schoolsLoading
                                    }
                                    onChange={handleChangeAutocomplete('schoolId')}
                                    getOptionLabel={getOptionLabel('shortName')}
                                    getOptionSelected={getOptionSelected()}
                                />
                            </>
                        ) : null}

                        <TextField
                            required
                            fullWidth
                            id='fullName'
                            label={t('common:forms.labels.full_name')}
                            placeholder={t('students:form.placeholder.typing_full_name') ?? ''}
                            value={values.fullName}
                            error={!!errors.fullName}
                            helperText={errors.fullName}
                            onChange={handleChange}
                            onBlur={handleBlur}
                        />

                        <TextField
                            required
                            fullWidth
                            id='email'
                            label={t('common:forms.labels.email')}
                            placeholder={t('common:forms.placeholder.typing_email') ?? ''}
                            value={values.email}
                            error={!!errors.email}
                            helperText={errors.email}
                            onChange={handleChange}
                            onBlur={handleBlur}
                        />

                        <Autocomplete<AutocompleteOption | null, false, false, false>
                            required
                            id='gender'
                            label={t('common:forms.labels.gender') ?? ''}
                            placeholder={t('common:forms.placeholder.choose_gender') ?? ''}
                            options={gendersOptions}
                            value={getGenderOptionValue(values.gender)}
                            error={!!errors.gender}
                            helperText={errors.gender}
                            onChange={handleChangeAutocomplete('gender', 'key')}
                            getOptionLabel={getOptionLabel('value', true)}
                            getOptionSelected={getOptionSelected()}
                        />

                        <DatepickerSinglePicker
                            date={values.birthdate}
                            onDateChange={handleChangeDate}
                        >
                            {datepickerProps => {
                                const { inputProps, inputComponent } = datepickerProps;

                                return (
                                    <DatepickerTextField
                                        required
                                        id='birthdate'
                                        label={t('common:forms.labels.birthdate') ?? ''}
                                        placeholder={t('common:forms.labels.date') ?? ''}
                                        error={!!errors.birthdate}
                                        helperText={errors.birthdate}
                                        InputProps={{ inputComponent }}
                                        {...inputProps}
                                    />
                                );
                            }}
                        </DatepickerSinglePicker>

                        <FormRowSubmit>
                            <Button variant='outlined' disabled={isSubmitting} onClick={onCancel}>
                                {t('common:actions.cancel')}
                            </Button>

                            <Button type='submit' loading={isSubmitting}>
                                {t('common:actions.add')}
                            </Button>
                        </FormRowSubmit>
                    </StyledForm>
                );
            }}
        </ExtendedFormik>
    );
};

export { StudentCreateForm };
